import { ReactNode } from "react";
import React from "react";
import {
  Control,
  DefaultValues,
  FieldErrors,
  FieldValues,
  Path,
  PathValue,
  RegisterOptions,
  UseFormRegister,
  useForm,
} from "react-hook-form";
import styled from "styled-components";
import {
  Divider as BaseDivider,
  Body,
  ButtonProps,
  CustomAction,
  Heading,
  SelectOption,
  Skeleton,
  maxSmall,
} from "@songtradr-one/components-frontend";
import { staticText } from "../../statics/static-text";
import { EntryAction, EntryAddAction } from "../entry-list/EntryList.Parts";
import { FormDialogConfig } from "../form-dialog/FormDialog.Parts";
import { FormField } from "../form-field/FormField";
import { NavigationGuardConfig } from "./use-form-navigation-guard/useFormNavigationGuard.Parts";

// TEXT
export const text = staticText.form;

// STYLES
export const FormWrapper = styled.div<{
  $maxWidth?: string;
}>`
  width: 100%;
  margin: 0 auto;
  max-width: ${({ $maxWidth }) => $maxWidth || "100%"};
`;

export const Divider = styled(BaseDivider)<{
  $shade?: string;
  $variant?: "normal" | "noMargin";
}>`
  ${({ $variant }) => $variant && $variant === "normal" && `margin: 0.5rem 0;`}
  ${({ $shade }) => $shade && `background: ${$shade};`}
`;

export const FormSectionWrapper = styled.div<{
  $columns?: number;
  $gap?: string;
}>`
  width: 100%;
  display: grid;
  gap: ${({ $gap }) => $gap || "1.5rem"};
  grid-template-columns: repeat(${({ $columns }) => $columns || 2}, 1fr);
  margin-bottom: 1.5rem;

  /* Mobile responsiveness - single column on small screens */
  @media ${maxSmall} {
    grid-template-columns: 1fr;
  }
`;

export const FormSectionTitle = styled(Heading).attrs({
  variant: "h3-strong",
})<{ $margin?: boolean }>`
  grid-column: 1 / -1;
  margin: ${({ $margin }) => ($margin ? "3rem 0 0" : "0")};
`;

export const FormSectionSubtitle = styled(Heading).attrs({ variant: "h4" })`
  grid-column: 1 / -1;
  margin: -1rem 0 0;
  opacity: 0.8;
`;

export const FormSectionDescription = styled(Body)`
  grid-column: 1 / -1;
  margin: -1rem 0 0;
  opacity: 0.8;
`;

export const FormSectionHeader = styled(Heading).attrs({ variant: "section" })`
  margin: 0;
  margin: 0 0 -0.5rem;
`;

export const FormFieldContainer = styled.div<{
  $columnSpan?: number;
  $columns?: number;
}>`
  grid-column: span
    ${({ $columnSpan, $columns }) =>
      $columnSpan ? Math.min($columnSpan, $columns || 2) : 1};

  /* Mobile responsiveness - always span full width on small screens */
  @media ${maxSmall} {
    grid-column: 1 / -1;
  }
`;

export const CustomFieldContainer = styled.div`
  display: flex;
  flex: 1;
`;

export const FormActionsContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  padding-top: 1rem;
`;

export const FormActionsRight = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-left: auto;
`;

export const ToggleSkeleton = styled(Skeleton)`
  border-radius: 2rem;
  flex-shrink: 0;
`;

// DATA

// TYPES
export type FieldOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type BaseField<T extends FieldValues = FieldValues> = {
  label?: string;
  name: Path<T>;
  single?: boolean;
  required?: boolean;
  disabled?: boolean;
  columnSpan?: number;
  visible?: (data: T) => boolean;
  rules?: Omit<
    RegisterOptions<T, Path<T>>,
    "disabled" | "setValueAs" | "valueAsNumber" | "valueAsDate"
  >;
};

type TextField<T extends FieldValues = FieldValues> = BaseField<T> & {
  type: "text" | "email" | "password" | "number";
  placeholder?: string;
  readOnly?: boolean;
  copyable?: boolean;
  customActions?: CustomAction[];
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
};

type TextAreaField<T extends FieldValues = FieldValues> = BaseField<T> & {
  type: "textarea";
  placeholder?: string;
  readOnly?: boolean;
  rows?: number;
  initialHeight?: string;
  maxHeight?: string;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
};

type SelectField<T extends FieldValues = FieldValues> = BaseField<T> & {
  type: "select";
  placeholder?: string;
  search?: boolean;
  options?: FieldOption[];
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
};

type RadioField<T extends FieldValues = FieldValues> = BaseField<T> & {
  type: "radio";
  options: FieldOption[];
};

type CheckboxField<T extends FieldValues = FieldValues> = BaseField<T> & {
  type: "checkbox";
  options?: FieldOption[];
  readOnly?: boolean;
};

type ToggleField<T extends FieldValues = FieldValues> = BaseField<T> & {
  type: "toggle";
  size?: "small" | "large";
};

type DateField<T extends FieldValues = FieldValues> = BaseField<T> & {
  type: "date";
  placeholder?: string;
  readOnly?: boolean;
};

type SecretField<T extends FieldValues = FieldValues> = BaseField<T> & {
  type: "secret";
  placeholder?: string;
  readOnly?: boolean;
  copyable?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
};

type EntryListFieldOf<T extends FieldValues, N extends Path<T>, E> =
  E extends Record<string, unknown>
    ? BaseField<T> & {
        name: N;
        type: "entrylist";
        emptyText?: string;
        addDialogConfig?: FormDialogConfig<E> | (() => FormDialogConfig<E>);
        editDialogConfig?:
          | FormDialogConfig<E>
          | ((entry: E, index?: number) => FormDialogConfig<E>);
        onAddEntries?: () => Promise<E[] | void> | E[] | void;
        onReplaceEntries?: () => Promise<E[] | void> | E[] | void;
        addActions?: EntryAddAction<E>[];
        onEditEntry?: (entry: E, index: number) => Promise<E | void> | E | void;
        onEntriesChange?: (entries: E[]) => void;
        addButtonContent?: ReactNode;
        removeConfirmation?: (entry: E) => Promise<boolean> | boolean;
        labelProperty?: string & keyof E;
        displayValue?: (entry: E) => string;
        actions?: EntryAction<E>[];
      }
    : never;

type EntryListField<T extends FieldValues = FieldValues> = {
  [N in Path<T>]: EntryListFieldOf<
    T,
    N,
    PathValue<T, N> extends (infer E)[] ? E : never
  >;
}[Path<T>];

type DividerField<T extends FieldValues = FieldValues> = {
  type: "divider";
  shade?: string;
  variant?: "normal" | "noMargin";
  visible?: (data: T) => boolean;
};

type SectionHeaderField<T extends FieldValues = FieldValues> = {
  type: "sectionHeader";
  label: string;
  columnSpan?: number;
  visible?: (data: T) => boolean;
};

type CustomFieldRenderContext<T extends FieldValues = FieldValues> = {
  control: Control<T>;
};

type CustomField<T extends FieldValues = FieldValues> = {
  type: "custom";
  content: ReactNode | ((ctx: CustomFieldRenderContext<T>) => ReactNode);
  columnSpan?: number;
  visible?: (data: T) => boolean;
};

export type Field<T extends FieldValues = FieldValues> =
  | TextField<T>
  | TextAreaField<T>
  | SelectField<T>
  | RadioField<T>
  | CheckboxField<T>
  | ToggleField<T>
  | DateField<T>
  | SecretField<T>
  | EntryListField<T>
  | DividerField<T>
  | SectionHeaderField<T>
  | CustomField<T>;

export type FormSection<T extends FieldValues = FieldValues> = {
  title?: string;
  subtitle?: string;
  description?: string;
  fields: Field<T>[];
  columns?: number;
  gap?: string;
};

export type FormFieldConfig<T extends FieldValues> = Field<T>;

export type FormSubmissionData<T extends FieldValues> = {
  cleanData: Omit<T, "__typename">;
  changes: Partial<Omit<T, "__typename">>;
  rawData: T;
  changedFields: string[];
};

export type FormLayout = {
  maxWidth?: string;
  columns?: number;
  gap?: string;
};

export type FormProps<T extends FieldValues> = {
  fields?: FormFieldConfig<T>[];
  sections?: FormSection<T>[];
  defaultValues?: DefaultValues<T>;
  onSubmit?: (data: FormSubmissionData<T>) => void | Promise<void>;
  onValuesChange?: (values: T) => void;
  options?: Record<string, SelectOption[]>;
  layout?: FormLayout;
  submitButton?: {
    text?: string;
    loading?: boolean;
    disabled?: boolean;
    variant?: ButtonProps["$variant"];
    size?: ButtonProps["$size"];
    disableWhenUnchanged?: boolean;
    disableWhenInvalid?: boolean;
  };
  cancelButton?: {
    text?: string;
    onClick: () => void;
    disabled?: boolean;
    variant?: ButtonProps["$variant"];
    size?: ButtonProps["$size"];
  };
  actions?: ReactNode;
  children?: ReactNode;
  preventNavigationOnChanges?: boolean | NavigationGuardConfig;
};

export type FormMethods<T extends FieldValues> = {
  fillData: (data: Partial<T>) => void;
  reset: () => void;
  getValues: () => T;
};

// CONSTANTS
/** Mirrors the Toggle root dimensions per size */
export const TOGGLE_SKELETON_SIZE = {
  small: { $width: "2.25rem", $height: "1.5rem" },
  large: { $width: "3rem", $height: "2rem" },
};

// FUNCTIONS
export const removeTypename = <T extends object>(data: T): T => {
  if (Array.isArray(data)) {
    return data.map((item) => removeTypename(item)) as T;
  }
  if (typeof data === "object" && data !== null) {
    const filteredEntries = Object.entries(data).filter(
      ([key]) => key !== "__typename",
    );

    const processedEntries = filteredEntries.map(([key, value]) => [
      key,
      removeTypename(value),
    ]);
    return Object.fromEntries(processedEntries);
  }
  return data;
};

export const convertFieldsToSections = <T extends FieldValues>(
  fields?: Field<T>[],
  sections?: FormSection<T>[],
): FormSection<T>[] => {
  return sections || (fields ? [{ fields }] : []);
};

type InputField<T extends FieldValues> = Exclude<
  Field<T>,
  { type: "divider" } | { type: "sectionHeader" } | { type: "custom" }
>;

export const buildFormFieldProps = <T extends FieldValues>(
  field: InputField<T>,
  sectionColumns: number,
  control: Control<T>,
  register: UseFormRegister<T>,
  errors: FieldErrors<T>,
  options: Record<string, SelectOption[]>,
  defaultValues?: DefaultValues<T>,
): Parameters<typeof FormField<T, Path<T>>>[0] => {
  const fieldDefault = defaultValues
    ? (field.name as string)
        .split(".")
        .reduce(
          (obj: Record<string, unknown> | undefined, key: string) =>
            obj?.[key] as Record<string, unknown> | undefined,
          defaultValues as Record<string, unknown>,
        )
    : undefined;

  return {
    label: field.label,
    name: field.name,
    type: field.type,
    single: field.columnSpan === sectionColumns || field.single,
    disabled: field.disabled,
    defaultValue: fieldDefault,
    rules: field.rules,
    control,
    register,
    errors,
    options: ("options" in field && field.options) || options[field.name] || [],
    ...("search" in field && { search: field.search }),
    ...("placeholder" in field && { placeholder: field.placeholder }),
    ...("readOnly" in field && { readOnly: field.readOnly }),
    ...("copyable" in field && { copyable: field.copyable }),
    ...("customActions" in field && { customActions: field.customActions }),
    ...("options" in field && { fieldOptions: field.options }),
    ...("rows" in field && { rows: field.rows }),
    ...("onBlur" in field && { onBlur: field.onBlur }),
    ...("emptyText" in field && { emptyText: field.emptyText }),
    ...("addDialogConfig" in field && {
      addDialogConfig: field.addDialogConfig,
    }),
    ...("editDialogConfig" in field && {
      editDialogConfig: field.editDialogConfig,
    }),
    ...("onAddEntries" in field && { onAddEntries: field.onAddEntries }),
    ...("onReplaceEntries" in field && {
      onReplaceEntries: field.onReplaceEntries,
    }),
    ...("addActions" in field && { addActions: field.addActions }),
    ...("onEditEntry" in field && { onEditEntry: field.onEditEntry }),
    ...("onEntriesChange" in field && {
      onEntriesChange: field.onEntriesChange,
    }),
    ...("addButtonContent" in field && {
      addButtonContent: field.addButtonContent,
    }),
    ...("removeConfirmation" in field && {
      removeConfirmation: field.removeConfirmation,
    }),
    ...("labelProperty" in field && { labelProperty: field.labelProperty }),
    ...("displayValue" in field && { displayValue: field.displayValue }),
    ...("actions" in field && { actions: field.actions }),
    ...("size" in field && { size: field.size }),
  } as Parameters<typeof FormField<T, Path<T>>>[0];
};

export const useFormMethods = <T extends FieldValues>(
  defaultValues?: DefaultValues<T>,
): ReturnType<typeof useForm<T>> & FormMethods<T> => {
  const methods = useForm<T>({
    mode: "onTouched",
    defaultValues,
  });

  const fillData = (data: Partial<T>) => {
    Object.entries(data).forEach(([key, value]) => {
      methods.setValue(key as Path<T>, value);
    });
  };

  return {
    ...methods,
    fillData,
  };
};

// ASSETS
